import PropTypes from 'prop-types';
import { Col, Row, Select, TextField } from '@arkestro/arkestro-design-system';
import useTranslation from 'next-translate/useTranslation';
import InputPhone from 'components/shared/forms/InputPhone/InputPhone';
import { parseErrorsIntoString } from 'utils/password';
import { prettyBusinessRegionsMap } from '@utils/regions';
import { TooltipInfoIcon } from '@components/shared';
import styles from 'components/shared/forms/InputField/InputField.module.scss';

const BUSINESS_REGION_WIDTH = 300;
const EMPTY_FIELD_LISTS = [];

const readOnlySections = (fieldLists, listsName) => fieldLists.map(fieldList => (
  <section key={`section-${listsName}-${fieldList[0].key}`} className='section-container'>
    <Row>
      {fieldList.map(field => (
        <Col key={`col-${field.key}`} className='read-only' sm={24 / fieldList.length}>
          <label className='label' htmlFor={field.key}>{field.label}</label>
          <span className='text' id={field.key}><strong>{field.value}</strong></span>
        </Col>
      ))}
    </Row>
  </section>
));

const inputSections = (fieldLists, onChange, listsName, t) => fieldLists.map(fieldList => {
  const businessRegionMap = prettyBusinessRegionsMap(t);
  const datePartOrderOptions = [
    { label: 'MM/DD/YYYY', value: 'MDY' },
    { label: 'DD/MM/YYYY', value: 'DMY' },
    { label: 'YYYY/MM/DD', value: 'YMD' },
  ];

  return (
    <section key={`section-${listsName}-${fieldList[0].key}`} className='section-container'>
      <Row gutter={16}>
        {fieldList.map(inputField => {
          const error = parseErrorsIntoString(inputField.errors);
          // eslint-disable-next-line sort-keys-fix/sort-keys-fix
          const colSizes = inputField.colSizes || { sm: 24, md: 24 / fieldList.length };

          if (inputField.key === 'phone') {
            return (
              <Col key={`col-${inputField.key}`} sm={24 / fieldList.length}>
                <div style={{ width: BUSINESS_REGION_WIDTH }}>
                  <InputPhone onChange={onChange} value={inputField.value} />
                </div>
              </Col>
            );
          }

          if (inputField.key === 'businessRegion') {
            return (
              <Col key={`col-${inputField.key}`} sm={24 / fieldList.length}>
                <div style={{ alignItems: 'baseline', display: 'flex' }}>
                  <label
                    className={styles.label}
                    htmlFor='businessRegion'
                    style={{ marginRight: 5 }}
                  >
                    {t('general.business_region')}
                  </label>
                  <TooltipInfoIcon title={t('tooltip.business_region.description')} />
                </div>
                <Select
                  name='businessRegion'
                  onChange={val => onChange({ target: { name: 'businessRegion', value: val } })}
                  options={[
                    { label: businessRegionMap.na, value: 'na' },
                    { label: businessRegionMap.emea, value: 'emea' },
                    { label: businessRegionMap.latam, value: 'latam' },
                    { label: businessRegionMap.apac, value: 'apac' },
                    { label: '', value: '' },
                  ]}
                  style={{ width: BUSINESS_REGION_WIDTH }}
                  value={inputField.value}
                />
              </Col>
            );
          }
          if (inputField.key === 'datePartOrder') {
            return (
              <Col key={`col-${inputField.key}`} sm={24 / fieldList.length}>
                <div style={{ alignItems: 'baseline', display: 'flex' }}>
                  <label
                    className={styles.label}
                    htmlFor='datePartOrder'
                    style={{ marginRight: 5 }}
                  >
                    {t('general.date_part_order')}
                  </label>
                  <TooltipInfoIcon title={t('tooltip.date_part_order.description')} />
                </div>
                <Select
                  name='datePartOrder'
                  onChange={val => onChange({ target: { name: 'datePartOrder', value: val } })}
                  options={datePartOrderOptions}
                  style={{ width: BUSINESS_REGION_WIDTH }}
                  value={datePartOrderOptions.find(option => option.value === inputField.value)?.value || ''}
                />
              </Col>
            );
          }

          return (
            <Col key={`col-${inputField.key}`} {...colSizes}>
              <TextField
                caption={error}
                label={inputField.label}
                name={inputField.key}
                onChange={onChange}
                required={inputField.required}
                status={error ? 'error' : undefined}
                type={inputField.type}
                value={inputField.value}
              />
            </Col>
          );
        })}
      </Row>
    </section>
  );
});

const ProfileFields = ({
  isEditingReadOnlyFields = EMPTY_FIELD_LISTS,
  readOnlyFields = EMPTY_FIELD_LISTS,
  normalFields = EMPTY_FIELD_LISTS,
  isEditingFields = EMPTY_FIELD_LISTS,
  onChange,
  isEditing = false
}) => {
  const { t } = useTranslation('common');

  return (
    <>
      {isEditing && readOnlySections(isEditingReadOnlyFields, 'isEditReadOnly')}
      {readOnlySections(readOnlyFields, 'readOnly')}
      {inputSections(normalFields, onChange, 'input', t)}
      {isEditing && inputSections(isEditingFields, onChange, 'isEditInput', t)}
    </>
  );
};

ProfileFields.propTypes = {
  isEditing: PropTypes.bool,
  isEditingFields: PropTypes.arrayOf(PropTypes.arrayOf(PropTypes.shape())),
  isEditingReadOnlyFields: PropTypes.arrayOf(PropTypes.arrayOf(PropTypes.shape())),
  normalFields: PropTypes.arrayOf(PropTypes.arrayOf(PropTypes.shape())),
  onChange: PropTypes.func.isRequired,
  readOnlyFields: PropTypes.arrayOf(PropTypes.arrayOf(PropTypes.shape()))
};

export default ProfileFields;
